import { useCallback, useEffect, useState } from 'react';
import { pushSubscribeInput, pushUnsubscribeInput } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';
import { fetchVapidPublicKey } from './vapidTransport.js';
import {
  detectPushSupport,
  getExistingSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  type SubscriptionKeys,
} from './pushSubscription.js';

/**
 * F22 state machine, driven entirely by explicit user action (ux.md: "never
 * nag on load, explicit opt-in per UX rules" — mount-time checks below only
 * ever READ existing permission/subscription state, they never call
 * `Notification.requestPermission()` themselves).
 *
 * `push.subscribe`/`push.unsubscribe` are shipped ops
 * (`packages/schemas/src/ws.ts`) but — same documented situation as this
 * app's `useNotificationPrefs.ts` for `settings.updateNotificationPrefs` —
 * not yet entered into `WsOpContracts`
 * (`packages/ws-protocol/src/op-contracts.ts`, out of this task's ownership
 * boundary), so they're called via the raw `WsClient.request()` escape
 * hatch, validating the payload against the shared zod schema client-side
 * first (the same discipline `requestTyped` would give for free).
 */

export type PushPermissionState = 'default' | 'granted' | 'denied';
export type PushSupportState = 'checking' | 'supported' | 'unsupported';
export type PushActionState = 'idle' | 'enabling' | 'disabling' | 'error';

export interface UsePushSettingsResult {
  support: PushSupportState;
  permission: PushPermissionState;
  subscribed: boolean;
  actionState: PushActionState;
  /** Present only when `actionState === 'error'`. */
  errorReason: 'permission-denied' | 'unsupported' | 'no-registration' | 'network' | undefined;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

function readPermission(): PushPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'default';
  return Notification.permission;
}

export function usePushSettings(): UsePushSettingsResult {
  const { wsClient } = useWs();
  const [support, setSupport] = useState<PushSupportState>('checking');
  const [permission, setPermission] = useState<PushPermissionState>('default');
  const [subscribed, setSubscribed] = useState(false);
  const [actionState, setActionState] = useState<PushActionState>('idle');
  const [errorReason, setErrorReason] = useState<UsePushSettingsResult['errorReason']>(undefined);

  useEffect(() => {
    let cancelled = false;

    async function checkExistingState(): Promise<void> {
      const supportState = detectPushSupport() === 'supported' ? 'supported' : 'unsupported';
      if (cancelled) return;
      setSupport(supportState);
      setPermission(readPermission());
      if (supportState === 'unsupported') return;

      // READ-ONLY — never prompts (ux.md: no on-load permission nagging).
      const existing = await getExistingSubscription();
      if (!cancelled) setSubscribed(existing !== undefined);
    }

    void checkExistingState();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async (): Promise<void> => {
    setActionState('enabling');
    setErrorReason(undefined);
    // Tracks whether `subscribeToPush` actually created a browser
    // `PushSubscription` in this call, so the catch block below knows
    // whether there's local state to roll back.
    let createdSubscription = false;
    try {
      const vapidPublicKey = await fetchVapidPublicKey();
      const subscription: SubscriptionKeys = await subscribeToPush(vapidPublicKey);
      createdSubscription = true;
      const payload = pushSubscribeInput.parse(subscription);
      await wsClient.request('push.subscribe', payload);
      setPermission('granted');
      setSubscribed(true);
      setActionState('idle');
    } catch (err) {
      setPermission(readPermission());
      const reason =
        err instanceof Error && 'reason' in err
          ? (err as { reason: 'permission-denied' | 'unsupported' | 'no-registration' | 'subscribe-failed' }).reason
          : undefined;
      setErrorReason(reason === 'subscribe-failed' || reason === undefined ? 'network' : reason);
      setActionState('error');
      setSubscribed(false);
      // The browser subscription was created but the follow-up
      // `push.subscribe` WS call (server registration) failed — undo the
      // browser-side subscription so it doesn't outlive the failed
      // registration. Without this, the browser stays subscribed with no
      // matching `push_subscriptions` row, and the next mount's
      // `checkExistingState()` would wrongly report `subscribed: true`.
      if (createdSubscription) {
        await unsubscribeFromPush().catch(() => {
          // Best-effort rollback; the original error is what the caller sees.
        });
      }
      throw err;
    }
  }, [wsClient]);

  const disable = useCallback(async (): Promise<void> => {
    setActionState('disabling');
    setErrorReason(undefined);
    try {
      const existing = await getExistingSubscription();
      await unsubscribeFromPush();
      if (existing) {
        const payload = pushUnsubscribeInput.parse({ endpoint: existing.endpoint });
        await wsClient.request('push.unsubscribe', payload);
      }
      setSubscribed(false);
      setActionState('idle');
    } catch (err) {
      setErrorReason('network');
      setActionState('error');
      throw err;
    }
  }, [wsClient]);

  return { support, permission, subscribed, actionState, errorReason, enable, disable };
}
